/* Phone Shop — Client-side facet filtering, URL sync, infinite scroll, mobile drawer.
 *
 * - Reads URL query params on load and checks the matching facet inputs.
 * - On any facet change, updates the URL via pushState (no full page reload),
 *   then runs the filter pass that hides cards not matching the active filters.
 * - Updates the visible product count + the mobile filter badge.
 * - Infinite scrolls the next paginated page via Section Rendering API and
 *   re-applies the active filters to newly appended cards.
 *
 * URL params recognized:
 *   brand=Apple,Samsung
 *   model=iPhone%2015,Pixel%209%20Pro
 *   color=Black,Blue
 *   capacity=128GB,256GB
 *   min_price=200&max_price=1500
 *   in_stock=1
 *   sort_by=featured|best-selling|price-ascending|price-descending
 *
 * "featured" is a custom sort implemented entirely client-side: each card
 * carries `data-featured-rank` (1-based position in /collections/featured,
 * or 9999 for products that aren't featured). Shopify ignores `featured` as
 * a `sort_by` server-side, so we intercept in applySorting() below and
 * reorder the DOM after the page loads.
 */

(function () {
  'use strict';

  const AUTO_LOAD_LIMIT = 3;

  /* User-facing strings are read from `data-text-*` attributes on the
   * `[data-infinite-scroll]` root, which the Liquid template populates from
   * the active locale file. This keeps all user-visible copy translatable
   * without hardcoding English in the script. */
  function getText(key, fallback) {
    const root = document.querySelector('[data-infinite-scroll]');
    if (root && root.dataset[key]) return root.dataset[key];
    return fallback;
  }

  /* ---------------------------- Filter state ---------------------------- */

  const FACET_KEYS = ['brand', 'model', 'color', 'capacity'];
  const cardMetaCache = new WeakMap();

  function readStateFromURL() {
    const p = new URLSearchParams(window.location.search);
    /* Fall back to the sort dropdown's server-rendered selection when the URL
     * has no explicit `sort_by`. Without this, a fresh visit to a collection
     * where the template sets the default to our custom "featured" sort would
     * show the select labelled "Featured" while the products stay in
     * Shopify's default order (because the server has no way to honour our
     * custom sort value). Reading the select ensures the client re-orders on
     * first paint too. */
    let sortBy = p.get('sort_by') || '';
    if (!sortBy) {
      const sel = document.querySelector('[data-phone-sort]');
      if (sel && sel.value) sortBy = sel.value;
    }
    const state = {
      brand: parseList(p.get('brand')),
      model: parseList(p.get('model')),
      color: normalizeColorList(parseList(p.get('color'))),
      capacity: parseList(p.get('capacity')),
      min_price: parseNum(p.get('min_price')),
      max_price: parseNum(p.get('max_price')),
      in_stock: p.get('in_stock') === '1' || p.get('in_stock') === 'true',
      sort_by: sortBy,
    };
    return state;
  }

  function parseList(v) {
    if (!v) return [];
    return v.split(',').map((x) => x.trim()).filter(Boolean);
  }

  function lowerList(arr) {
    return arr.map((v) => v.toLowerCase());
  }

  let colorGroupsCache;

  function getColorGroups() {
    if (colorGroupsCache) return colorGroupsCache;

    const source = document.querySelector('[data-color-group-rules]');
    const raw = source ? source.getAttribute('data-color-group-rules') || '' : '';

    colorGroupsCache = raw
      .split('|')
      .map((rule) => {
        const [label, , aliases] = rule.split('~').map((part) => (part || '').trim());
        return {
          value: label.toLowerCase(),
          aliases: (aliases || '')
            .split(',')
            .map((alias) => alias.trim().toLowerCase())
            .filter(Boolean),
        };
      })
      .filter((group) => group.value && group.aliases.length);

    return colorGroupsCache;
  }

  function normalizeColorValue(value) {
    const v = String(value || '').trim().toLowerCase();
    if (!v) return '';

    const group = getColorGroups().find(({ aliases }) => aliases.some((alias) => v.includes(alias)));
    if (group) return group.value;

    return v
      .replace(/\b(awesome|new|light|dark|deep)\b/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function normalizeColorList(values) {
    return Array.from(new Set(values.map(normalizeColorValue).filter(Boolean)));
  }

  function normalizeState(state) {
    const normalized = {};
    FACET_KEYS.forEach((key) => {
      normalized[key] = key === 'color' ? normalizeColorList(state[key]) : lowerList(state[key]);
    });
    return normalized;
  }

  function parseNum(v) {
    if (v === null || v === undefined || v === '') return null;
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
  }

  function buildURL(state) {
    const p = new URLSearchParams();
    FACET_KEYS.forEach((k) => {
      if (state[k].length) p.set(k, state[k].join(','));
    });
    if (state.min_price !== null) p.set('min_price', state.min_price);
    if (state.max_price !== null) p.set('max_price', state.max_price);
    if (state.in_stock) p.set('in_stock', '1');
    if (state.sort_by) p.set('sort_by', state.sort_by);
    const qs = p.toString();
    return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  }

  function writeStateToURL(state, replace) {
    const url = buildURL(state);
    const method = replace ? 'replaceState' : 'pushState';
    window.history[method]({}, '', url);
  }

  function activeCount(state) {
    return (
      state.brand.length +
      state.model.length +
      state.color.length +
      state.capacity.length +
      (state.min_price !== null || state.max_price !== null ? 1 : 0) +
      (state.in_stock ? 1 : 0)
    );
  }

  /* ---------------------------- Filtering ---------------------------- */

  function getCollectionRoot(from) {
    return (from && from.closest && from.closest('.phone-collection')) || document.querySelector('.phone-collection') || document;
  }

  function getCards(root) {
    return Array.from(root.querySelectorAll('[data-product-grid] [data-card]'));
  }

  function readCardMeta(card) {
    const cached = cardMetaCache.get(card);
    if (cached) return cached;

    const meta = {
      brand: card.dataset.brand || '',
      models: (card.dataset.models || '').split('|').filter(Boolean),
      colors: normalizeColorList((card.dataset.colors || '').split('|').filter(Boolean)),
      capacities: (card.dataset.capacities || '').split('|').filter(Boolean),
      price: parseFloat(card.dataset.price || '0'),
      available: card.dataset.available === '1',
      title: card.dataset.title || '',
      created: parseInt(card.dataset.created || '0', 10),
      featuredRank: parseInt(card.dataset.featuredRank || '9999', 10),
    };
    cardMetaCache.set(card, meta);
    return meta;
  }

  function cardMatches(card, state, normalized) {
    const meta = readCardMeta(card);

    if (state.brand.length && !normalized.brand.includes(meta.brand)) return false;
    if (state.model.length && !normalized.model.some((m) => meta.models.includes(m))) return false;
    if (state.color.length && !normalized.color.some((c) => meta.colors.includes(c))) return false;
    if (state.capacity.length && !normalized.capacity.some((c) => meta.capacities.includes(c))) return false;
    if (state.min_price !== null && meta.price < state.min_price) return false;
    if (state.max_price !== null && meta.price > state.max_price) return false;
    if (state.in_stock && !meta.available) return false;

    return true;
  }

  function applyFilters(state, root) {
    const collectionRoot = root || getCollectionRoot();
    const cards = getCards(collectionRoot);
    const normalized = normalizeState(state);
    let visible = 0;
    cards.forEach((card) => {
      const match = cardMatches(card, state, normalized);
      card.hidden = !match;
      if (match) visible += 1;
    });

    applySorting(state.sort_by, collectionRoot);
    updateCount(visible, state);
    updateActiveCountBadge(state);
    updatePillsAndCounts(state);
    updateEmptyState(visible === 0);
  }

  function applySorting(sortBy, root) {
    if (!sortBy) return;
    const collectionRoot = root || getCollectionRoot();
    const grid = collectionRoot.querySelector('[data-product-grid]');
    if (!grid) return;
    const cards = getCards(collectionRoot);

    const cmp = (a, b) => {
      const ma = readCardMeta(a);
      const mb = readCardMeta(b);
      switch (sortBy) {
        case 'featured': {
          /* Lower rank = earlier in /collections/featured. Products that
           * aren't featured carry rank 9999 and naturally fall to the end.
           * We tie-break by creation date (newest first) so non-featured
           * products still have a stable, intuitive order. */
          if (ma.featuredRank !== mb.featuredRank) return ma.featuredRank - mb.featuredRank;
          return mb.created - ma.created;
        }
        case 'price-ascending':
          return ma.price - mb.price;
        case 'price-descending':
          return mb.price - ma.price;
        case 'title-ascending':
          return ma.title.localeCompare(mb.title);
        case 'title-descending':
          return mb.title.localeCompare(ma.title);
        case 'created-ascending':
          return ma.created - mb.created;
        case 'created-descending':
        case 'created':
          return mb.created - ma.created;
        default:
          return 0;
      }
    };

    cards.sort(cmp).forEach((c) => grid.appendChild(c));
  }

  function updateCount(visible, state) {
    /* When no filter is active we want the label to advertise the *total*
     * size of the collection, not just the cards that happen to be in the
     * DOM right now. This matters as soon as the collection exceeds a single
     * paginated page: the user should see "120 products" up front and have
     * more cards stream in as they scroll, rather than the count starting at
     * 50 and jumping higher during scroll.
     *
     * When a filter is active we fall back to the loaded-and-matching count,
     * which is a closer proxy to "results for your filter". */
    const filtersActive = state ? activeCount(state) > 0 : false;
    document.querySelectorAll('[data-product-count]').forEach((el) => {
      const total = parseInt(el.dataset.productTotal || '', 10);
      const shown = !filtersActive && !isNaN(total) ? total : visible;

      /* Prefer the one/other template pair when provided (e.g. the mobile
       * drawer "Show {n} result(s)" button, which must localize correctly
       * for singular vs plural). Fall back to a single template, and
       * finally to numeric substitution in the existing text.
       *
       * Placeholder is `[count]` (not `{{count}}`) because Shopify's Liquid
       * parser rejects nested `{{` inside a string literal that's already
       * inside an outer `{{ ... }}` expression, which is how the templates
       * get emitted server-side. */
      const tmplOne = el.dataset.templateOne;
      const tmplOther = el.dataset.templateOther;
      if (tmplOne && tmplOther) {
        const tmpl = shown === 1 ? tmplOne : tmplOther;
        el.textContent = tmpl.replace('[count]', shown);
        return;
      }

      const tmpl = el.dataset.template;
      if (tmpl) {
        el.textContent = tmpl.replace('[count]', shown);
      } else {
        // Replace the integer at the start of existing text, or fall back to overwriting.
        const txt = el.textContent.trim();
        const m = txt.match(/^\d+/);
        if (m) {
          el.textContent = txt.replace(/^\d+/, String(shown));
        } else {
          el.textContent = `${shown}`;
        }
      }
    });
  }

  function updateActiveCountBadge(state) {
    const count = activeCount(state);
    document.querySelectorAll('[data-active-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
  }

  function updateEmptyState(empty) {
    document.querySelectorAll('[data-empty-state]').forEach((el) => {
      el.hidden = !empty;
    });
    document.querySelectorAll('[data-product-grid]').forEach((g) => {
      g.hidden = empty;
    });
  }

  /* ---------------------------- Active pills + group counts ---------------------------- */

  function updatePillsAndCounts(state) {
    document.querySelectorAll('[data-active-pills]').forEach((container) => {
      container.innerHTML = '';
      let total = 0;

      FACET_KEYS.forEach((key) => {
        state[key].forEach((val) => {
          total += 1;
          container.appendChild(makePill(key, val, displayLabel(key, val)));
        });
      });

      if (state.min_price !== null || state.max_price !== null) {
        total += 1;
        const lo = state.min_price !== null ? state.min_price : '';
        const hi = state.max_price !== null ? state.max_price : '';
        container.appendChild(makePill('price', '', `${lo}–${hi}`));
      }
      if (state.in_stock) {
        total += 1;
        container.appendChild(makePill('in_stock', '1', getText('textInStockPill', 'In stock')));
      }

      container.hidden = total === 0;
    });

    // Update per-group counts and selected highlight on chips/swatches.
    document.querySelectorAll('[data-facet-group]').forEach((group) => {
      const key = group.dataset.facetGroup;
      let count = 0;
      if (key === 'price') {
        count = state.min_price !== null || state.max_price !== null ? 1 : 0;
      } else if (key === 'in_stock') {
        count = state.in_stock ? 1 : 0;
      } else {
        count = state[key] ? state[key].length : 0;
      }
      const countEl = group.querySelector('[data-group-count]');
      if (countEl) countEl.textContent = count > 0 ? `(${count})` : '';
    });

    // Visual active state on chips/swatches.
    document.querySelectorAll('.phone-facets__chip input, .phone-facets__swatch input').forEach((input) => {
      input.closest('label').classList.toggle('is-active', input.checked);
    });

    // Show/hide the clear-all link.
    document.querySelectorAll('[data-phone-clear-all]').forEach((btn) => {
      btn.hidden = activeCount(state) === 0;
    });
  }

  function makePill(facet, value, label) {
    const a = document.createElement('button');
    a.type = 'button';
    a.className = 'phone-facets__pill';
    a.innerHTML = `${escapeHtml(label)} <span aria-hidden="true">×</span>`;
    a.addEventListener('click', () => {
      const state = readStateFromURL();
      if (facet === 'price') {
        state.min_price = null;
        state.max_price = null;
      } else if (facet === 'in_stock') {
        state.in_stock = false;
      } else {
        const lower = String(value).toLowerCase();
        state[facet] = state[facet].filter((v) => v.toLowerCase() !== lower);
      }
      writeStateToURL(state);
      syncInputsFromState(state);
      applyFilters(state);
    });
    return a;
  }

  // Find the original-case label for a value by looking at the matching facet input.
  function displayLabel(facet, value) {
    const lower = String(value).toLowerCase();
    const input = Array.from(document.querySelectorAll(`input[data-facet="${facet}"]`)).find(
      (i) => (i.value || '').toLowerCase() === lower
    );
    return input ? input.value : value;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  /* ---------------------------- Sync DOM inputs from state ---------------------------- */

  function syncInputsFromState(state) {
    document.querySelectorAll('input[data-facet]').forEach((input) => {
      const facet = input.dataset.facet;
      if (facet === 'min_price') {
        input.value = state.min_price !== null ? state.min_price : '';
      } else if (facet === 'max_price') {
        input.value = state.max_price !== null ? state.max_price : '';
      } else if (facet === 'in_stock') {
        input.checked = state.in_stock;
      } else if (FACET_KEYS.includes(facet)) {
        const v = (input.value || '').toLowerCase();
        input.checked = state[facet].some((x) =>
          facet === 'color' ? normalizeColorValue(x) === normalizeColorValue(v) : x.toLowerCase() === v
        );
      }
    });
    document.querySelectorAll('[data-phone-sort]').forEach((select) => {
      if (state.sort_by) select.value = state.sort_by;
    });
  }

  /* ---------------------------- Input change handler ---------------------------- */

  function bindFacetInputs() {
    let priceTimer = null;

    document.addEventListener('change', (e) => {
      const input = e.target.closest('input[data-facet]');
      if (!input) {
        const sort = e.target.closest('[data-phone-sort]');
        if (sort) {
          const state = readStateFromURL();
          state.sort_by = sort.value || '';
          /* Sort must round-trip through Shopify so the server re-orders the
           * entire collection — not just the currently-loaded (paginated)
           * cards in the DOM. Without this, a collection with >50 products
           * would only sort the first page client-side, which looks like
           * "sorting does nothing" for the user. */
          window.location.href = buildURL(state);
        }
        return;
      }
      handleFacetChange(input);
    });

    document.addEventListener('input', (e) => {
      const input = e.target.closest('input[data-facet]');
      if (!input) return;
      const facet = input.dataset.facet;
      if (facet === 'min_price' || facet === 'max_price') {
        clearTimeout(priceTimer);
        priceTimer = setTimeout(() => handleFacetChange(input), 350);
      }
    });
  }

  function handleFacetChange(input) {
    const facet = input.dataset.facet;
    const value = input.value;
    const state = readStateFromURL();

    if (facet === 'min_price') {
      state.min_price = value === '' ? null : parseFloat(value);
    } else if (facet === 'max_price') {
      state.max_price = value === '' ? null : parseFloat(value);
    } else if (facet === 'in_stock') {
      state.in_stock = input.checked;
    } else if (FACET_KEYS.includes(facet)) {
      const list = state[facet];
      const lower = facet === 'color' ? normalizeColorValue(value) : value.toLowerCase();
      if (input.checked) {
        if (!list.some((x) => (facet === 'color' ? normalizeColorValue(x) : x.toLowerCase()) === lower)) list.push(value);
      } else {
        state[facet] = list.filter((x) => (facet === 'color' ? normalizeColorValue(x) : x.toLowerCase()) !== lower);
      }
      // Sync the OTHER context (desktop/mobile) input so both stay in sync.
      document.querySelectorAll(`input[data-facet="${facet}"][value="${escapeAttr(value)}"]`).forEach((other) => {
        if (other !== input) other.checked = input.checked;
      });
    }

    writeStateToURL(state);
    applyFilters(state);
  }

  function escapeAttr(s) {
    return String(s).replace(/"/g, '\\"');
  }

  /* ---------------------------- Clear all ---------------------------- */

  function bindClearAll() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-phone-clear-all]');
      if (!btn) return;
      const empty = { brand: [], model: [], color: [], capacity: [], min_price: null, max_price: null, in_stock: false, sort_by: readStateFromURL().sort_by };
      writeStateToURL(empty);
      syncInputsFromState(empty);
      applyFilters(empty);
    });
  }

  /* ---------------------------- Model search ---------------------------- */

  function bindModelSearch() {
    document.addEventListener('input', (e) => {
      const input = e.target.closest('[data-phone-facets-filter]');
      if (!input) return;
      const targetId = input.getAttribute('data-target');
      const list = document.getElementById(targetId);
      if (!list) return;
      const term = input.value.trim().toLowerCase();
      list.querySelectorAll('[data-search-text]').forEach((label) => {
        const text = label.getAttribute('data-search-text') || '';
        label.parentElement.hidden = term && !text.includes(term);
      });
    });
  }

  /* ---------------------------- Mobile drawer ---------------------------- */

  function bindMobileDrawer() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-phone-drawer-open]');
      if (opener) {
        const id = opener.getAttribute('data-phone-drawer-open');
        const drawer = document.getElementById(id);
        if (drawer) {
          drawer.setAttribute('aria-hidden', 'false');
          document.body.classList.add('phone-facets-drawer-open');
        }
        return;
      }
      const closer = e.target.closest('[data-phone-drawer-close]');
      if (closer) {
        const drawer = closer.closest('.phone-facets-drawer');
        if (drawer) {
          drawer.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('phone-facets-drawer-open');
        }
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('.phone-facets-drawer[aria-hidden="false"]').forEach((d) => {
        d.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('phone-facets-drawer-open');
      });
    });
  }

  /* ---------------------------- Infinite scroll ---------------------------- */

  class PhoneInfiniteScroll {
    constructor(root) {
      this.root = root;
      this.sectionId = root.getAttribute('data-section-id');
      this.grid = root.querySelector('[data-product-grid]');
      this.sentinel = root.querySelector('[data-load-sentinel]');
      this.button = root.querySelector('[data-load-more-button]');
      this.status = root.querySelector('[data-load-status]');
      this.spinner = root.querySelector('[data-load-spinner]');
      this.autoLoads = 0;
      this.isLoading = false;
      this.nextUrl = root.getAttribute('data-next-url') || null;

      if (!this.grid || !this.nextUrl) return;

      if (this.button) {
        this.button.addEventListener('click', () => this.loadNext(true));
      }

      if (this.sentinel && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.handleSentinelHit();
            });
          },
          { rootMargin: '600px 0px' }
        );
        this.observer.observe(this.sentinel);
      } else if (this.button) {
        this.showButton();
      }
    }

    handleSentinelHit() {
      if (!this.nextUrl) return;
      if (this.autoLoads >= AUTO_LOAD_LIMIT) {
        this.showButton();
        return;
      }
      this.loadNext(false);
    }

    showButton() {
      if (this.button) this.button.hidden = false;
      if (this.sentinel) this.sentinel.hidden = true;
      if (this.observer) this.observer.disconnect();
    }

    hideButton() {
      if (this.button) this.button.hidden = true;
      if (this.spinner) this.spinner.hidden = true;
    }

    async loadNext(manual) {
      if (this.isLoading || !this.nextUrl) return;
      this.isLoading = true;
      this.hideButton();
      if (this.spinner) this.spinner.hidden = false;
      if (this.status) this.status.textContent = '';

      try {
        const url = new URL(this.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);
        const res = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const html = await res.text();

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = doc.querySelector('[data-product-grid]');
        const newRoot = doc.querySelector('[data-infinite-scroll]');

        if (newGrid) {
          const fragment = document.createDocumentFragment();
          Array.from(newGrid.children).forEach((child) => fragment.appendChild(child));
          this.grid.appendChild(fragment);
          // Re-apply the active filters to the newly appended cards.
          applyFilters(readStateFromURL(), this.root.closest('.phone-collection'));
        }

        this.nextUrl = newRoot ? newRoot.getAttribute('data-next-url') : null;
        this.root.setAttribute('data-next-url', this.nextUrl || '');

        if (!manual) this.autoLoads += 1;
      } catch (err) {
        console.error('Phone infinite scroll error:', err);
        if (this.status) {
          this.status.textContent = getText('textLoadError', 'Could not load more. Please try again.');
        }
      } finally {
        this.isLoading = false;
        if (this.spinner) this.spinner.hidden = true;
        if (!this.nextUrl) {
          this.hideButton();
          if (this.sentinel) this.sentinel.hidden = true;
        } else if (this.autoLoads >= AUTO_LOAD_LIMIT) {
          this.showButton();
        }
      }
    }
  }

  /* ---------------------------- Init ---------------------------- */

  function init() {
    const state = readStateFromURL();
    syncInputsFromState(state);
    applyFilters(state);
    bindFacetInputs();
    bindClearAll();
    bindModelSearch();
    bindMobileDrawer();

    document.querySelectorAll('[data-infinite-scroll]').forEach((root) => new PhoneInfiniteScroll(root));

    // React to back/forward navigation.
    window.addEventListener('popstate', () => {
      const s = readStateFromURL();
      syncInputsFromState(s);
      applyFilters(s);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
